import React from 'react';
import { Check, DeleteOutline } from '@mui/icons-material';
import { Colors } from 'library';
import {
    RCard,
    RChip,
    RIconButton,
    RRow,
    RStack,
    RText,
    RTextButton,
} from 'recruit/components';
import { PaymentMethodBadge } from './PaymentMethodBadge';
import { PaymentMethod, isBankAccount, isCreditCard } from './types';

interface PaymentMethodRowProps {
    method: PaymentMethod;
    showSubsDefault: boolean;
    showRecruitDefault: boolean;
    recruitDefaultLabel?: string;
    setRecruitDefaultLabel?: string;
    onSetSubsDefault: () => void;
    onSetRecruitDefault: () => void;
    onDelete: () => void;
}

export function PaymentMethodRow({
    method,
    showSubsDefault,
    showRecruitDefault,
    recruitDefaultLabel = 'Recruit Default',
    setRecruitDefaultLabel = 'Set Recruit Default',
    onSetSubsDefault,
    onSetRecruitDefault,
    onDelete,
}: PaymentMethodRowProps) {
    const isBank = isBankAccount(method);
    const isCard = isCreditCard(method);

    const primaryLabel = isBank
        ? `${method.bank_name || 'Bank account'} •••• ${method.last4 || '????'}`
        : `•••• •••• •••• ${method.last4 || '????'}`;

    const secondaryLabel = isCard
        ? `${method.cardholder_name ? `${method.cardholder_name} · ` : ''}Expires ${String(method.exp_month ?? '').padStart(2, '0')}/${method.exp_year ?? ''}`
        : (method.cardholder_name ?? '');

    return (
        <RCard padding="md">
            <RRow justify="between" align="center">
                <RRow gap="md" align="center">
                    <PaymentMethodBadge method={method} />
                    <RStack gap="none">
                        <RText weight="semibold">{primaryLabel}</RText>
                        {secondaryLabel && (
                            <RText size="sm" tone="secondary">
                                {secondaryLabel}
                            </RText>
                        )}
                    </RStack>
                </RRow>
                <RRow gap="md" align="center">
                    {showSubsDefault && method.is_default && <DefaultChip label="Default" />}
                    {showRecruitDefault && method.is_recruit_default && <DefaultChip label={recruitDefaultLabel} />}
                    {showSubsDefault && !method.is_default && (
                        <RTextButton onClick={onSetSubsDefault} color={Colors.mediumGrey}>
                            Set Default
                        </RTextButton>
                    )}
                    {showRecruitDefault && !method.is_recruit_default && isCard && (
                        <RTextButton onClick={onSetRecruitDefault} color={Colors.mediumGrey}>
                            {setRecruitDefaultLabel}
                        </RTextButton>
                    )}
                    <RIconButton icon={DeleteOutline} onClick={onDelete} />
                </RRow>
            </RRow>
        </RCard>
    );
}

function DefaultChip({ label }: { label: string }) {
    return (
        <RChip
            variant="outlined"
            bgColor={Colors.fill}
            textColor={Colors.darkNavy}
            borderColor={Colors.lightGrey}
        >
            <Check sx={{ fontSize: 14 }} />
            {label}
        </RChip>
    );
}
